import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Badge from "@/components/base/Badge";
import DocumentPdfButton from "@/components/base/DocumentPdfButton";
import PrintButton from "@/components/base/PrintButton";
import ConfirmDeleteModal from "@/components/base/ConfirmDeleteModal";
import EmptyState from "@/components/base/EmptyState";
import ImmeubleForm from "../components/ImmeubleForm";
import { useEmployes } from "@/pages/agence/employes/EmployesProvider";
import { useAgenceData } from "@/pages/agence/data/AgenceDataProvider";
import {
  AGENCE_BIEN_STATUT_LABELS,
  AGENCE_BIEN_STATUT_TONES,
  AGENCE_BIEN_TYPE_LABELS,
  AGENCE_CONTRAT_STATUT_LABELS,
  AGENCE_CONTRAT_STATUT_TONES,
  agenceContratReference,
  agenceLocataireNomComplet,
} from "@/pages/agence/data/types";
import { useProprietaires } from "@/pages/agence/proprietaires/ProprietairesProvider";
import { proprietaireNomComplet } from "@/pages/agence/proprietaires/types";
import { formatDate, formatFcfa } from "@/utils/format";
import { exportSummaryPdf, printSummaryPdf, type PdfDocumentOptions, type PdfSection } from "@/utils/pdfDocument";

export default function AgenceImmeubleDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { getImmeuble, biensByImmeuble, contratsByImmeuble, getLocataire, getBien, removeImmeuble } = useAgenceData();
  const { getProprietaire } = useProprietaires();
  const { can } = useEmployes();
  const navigate = useNavigate();
  const [editOpen, setEditOpen] = useState(false);
  const [toDelete, setToDelete] = useState(false);
  const immeuble = id ? getImmeuble(id) : undefined;

  if (!immeuble) {
    return (
      <div className="flex flex-col gap-6">
        <Link
          to="/agence/immeubles"
          className="inline-flex w-fit items-center gap-2 whitespace-nowrap text-sm font-label font-medium text-foreground-600 transition-colors hover:text-foreground-950 cursor-pointer"
        >
          <i className="ri-arrow-left-line text-base" aria-hidden="true" />
          Retour aux immeubles
        </Link>
        <EmptyState
          icon="ri-building-2-line"
          title="Immeuble introuvable"
          description="Cet immeuble n'existe pas ou n'est plus disponible. Retournez à la liste pour le retrouver."
          action={
            <Link
              to="/agence/immeubles"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-arrow-left-line text-base" aria-hidden="true" />
              Voir tous les immeubles
            </Link>
          }
        />
      </div>
    );
  }

  const proprietaire = getProprietaire(immeuble.proprietaireId);
  const biens = biensByImmeuble(immeuble.id);
  const contrats = contratsByImmeuble(immeuble.id);
  const locataires = Array.from(new Set(contrats.map((contrat) => contrat.locataireId)))
    .map((locataireId) => getLocataire(locataireId))
    .filter((locataire): locataire is NonNullable<typeof locataire> => Boolean(locataire));

  const localisation = [immeuble.commune, immeuble.province, immeuble.region].filter(Boolean).join(" · ");
  const niveauxLabel = `${immeuble.niveaux} niveau${immeuble.niveaux > 1 ? "x" : ""}`;

  const infos: { label: string; value: string; icon: string }[] = [
    { label: "Région", value: immeuble.region || "—", icon: "ri-map-2-line" },
    { label: "Province", value: immeuble.province || "—", icon: "ri-community-line" },
    { label: "Commune / Ville", value: immeuble.commune || "—", icon: "ri-building-line" },
    { label: "Secteur / Quartier", value: immeuble.secteur || "—", icon: "ri-map-pin-2-line" },
    { label: "Adresse", value: immeuble.adresse || "—", icon: "ri-road-map-line" },
    { label: "Nombre de niveaux", value: niveauxLabel, icon: "ri-stack-line" },
  ];

  const buildPdfOptions = (): PdfDocumentOptions => {
    const sections: PdfSection[] = [
      {
        heading: "Informations de l'immeuble",
        fields: [
          { label: "Nom", value: immeuble.nom },
          ...infos.map((info) => ({ label: info.label, value: info.value })),
          {
            label: "Propriétaire",
            value: proprietaire ? proprietaireNomComplet(proprietaire) : "Non renseigné",
          },
          { label: "Date d'ajout", value: formatDate(immeuble.createdAt) },
        ],
      },
    ];
    if (immeuble.description) {
      sections.push({ heading: "Description", note: immeuble.description });
    }
    if (immeuble.infosComplementaires) {
      sections.push({ heading: "Informations complémentaires", note: immeuble.infosComplementaires });
    }
    sections.push({
      heading: "Synthèse",
      fields: [
        { label: "Biens", value: String(biens.length) },
        { label: "Locataires", value: String(locataires.length) },
        { label: "Contrats", value: String(contrats.length) },
        {
          label: "Loyers potentiels / mois",
          value: formatFcfa(biens.reduce((total, bien) => total + bien.loyerMensuel, 0)),
        },
      ],
    });
    if (biens.length > 0) {
      sections.push({
        heading: "Biens de l'immeuble",
        table: {
          columns: ["Référence", "Type", "Loyer / mois", "Statut"],
          rows: biens.map((bien) => [
            bien.reference || AGENCE_BIEN_TYPE_LABELS[bien.type],
            AGENCE_BIEN_TYPE_LABELS[bien.type],
            formatFcfa(bien.loyerMensuel),
            AGENCE_BIEN_STATUT_LABELS[bien.statut],
          ]),
        },
      });
    }
    if (contrats.length > 0) {
      sections.push({
        heading: "Contrats de l'immeuble",
        table: {
          columns: ["Référence", "Bien", "Locataire", "Loyer / mois", "Statut"],
          rows: contrats.map((contrat) => {
            const bien = getBien(contrat.bienId);
            const locataire = getLocataire(contrat.locataireId);
            return [
              agenceContratReference(contrat.id),
              bien?.reference || (bien ? AGENCE_BIEN_TYPE_LABELS[bien.type] : "—"),
              locataire ? agenceLocataireNomComplet(locataire) : "—",
              formatFcfa(contrat.loyerMensuel),
              AGENCE_CONTRAT_STATUT_LABELS[contrat.statut],
            ];
          }),
        },
      });
    }
    return {
      filename: `immeuble-${immeuble.nom}`,
      title: immeuble.nom,
      subtitle: localisation || "Immeuble",
      sections,
    };
  };

  const handleExportPdf = () => exportSummaryPdf(buildPdfOptions());
  const handlePrint = () => printSummaryPdf(buildPdfOptions());

  return (
    <div className="flex flex-col gap-6">
      <Link
        to="/agence/immeubles"
        className="inline-flex w-fit items-center gap-2 whitespace-nowrap text-sm font-label font-medium text-foreground-600 transition-colors hover:text-foreground-950 cursor-pointer"
      >
        <i className="ri-arrow-left-line text-base" aria-hidden="true" />
        Retour aux immeubles
      </Link>

      <section className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
        <div className="relative h-48 w-full overflow-hidden bg-background-100 md:h-64">
          {immeuble.photo ? (
            <img
              src={immeuble.photo}
              alt={immeuble.nom}
              title={`${immeuble.nom} — immeuble à ${immeuble.commune}`}
              className="h-48 w-full object-cover object-top md:h-64"
            />
          ) : (
            <div className="flex h-48 w-full items-center justify-center bg-background-100 text-foreground-300 md:h-64">
              <i className="ri-building-2-line text-6xl" aria-hidden="true" />
            </div>
          )}
        </div>

        <div className="flex flex-col gap-4 p-4 md:p-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone="primary" icon="ri-user-star-line">
                {proprietaire ? proprietaireNomComplet(proprietaire) : "Propriétaire non défini"}
              </Badge>
              <Badge tone="accent" icon="ri-stack-line">
                {niveauxLabel}
              </Badge>
            </div>
            <h2 className="mt-3 font-heading text-xl font-semibold text-foreground-950 md:text-2xl">{immeuble.nom}</h2>
            <p className="mt-1.5 flex items-center gap-1.5 text-sm text-foreground-600">
              <i className="ri-map-pin-line text-base text-foreground-400" aria-hidden="true" />
              {localisation || "Localisation non renseignée"}
            </p>
            <p className="mt-1 text-xs text-foreground-400">Ajouté le {formatDate(immeuble.createdAt)}</p>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row lg:shrink-0">
            <Link
              to={`/agence/biens?immeuble=${immeuble.id}&nouveau=1`}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-add-line text-base" aria-hidden="true" />
              Ajouter un bien
            </Link>
            {proprietaire && (
              <Link
                to={`/agence/proprietaires/${proprietaire.id}`}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-4 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
              >
                <i className="ri-user-star-line text-base" aria-hidden="true" />
                Voir le propriétaire
              </Link>
            )}
            <DocumentPdfButton onExport={handleExportPdf} />
            <PrintButton onPrint={handlePrint} />
            {can("immeubles.modifier") && (
              <button
                type="button"
                onClick={() => setEditOpen(true)}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-4 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
              >
                <i className="ri-edit-line text-base" aria-hidden="true" />
                Modifier
              </button>
            )}
            {can("immeubles.supprimer") && (
              <button
                type="button"
                onClick={() => setToDelete(true)}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-primary-200 bg-background-50 px-4 py-2.5 text-sm font-label font-medium text-primary-700 transition-colors hover:bg-primary-50 cursor-pointer"
              >
                <i className="ri-delete-bin-line text-base" aria-hidden="true" />
                Supprimer
              </button>
            )}
          </div>
        </div>
      </section>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
            <i className="ri-home-4-line text-lg" aria-hidden="true" />
          </span>
          <p className="mt-4 font-heading text-2xl font-semibold leading-none text-foreground-950">{biens.length}</p>
          <p className="mt-1.5 text-xs text-foreground-600 md:text-sm">Biens</p>
        </div>
        <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-secondary-50 text-secondary-600">
            <i className="ri-user-3-line text-lg" aria-hidden="true" />
          </span>
          <p className="mt-4 font-heading text-2xl font-semibold leading-none text-foreground-950">
            {locataires.length}
          </p>
          <p className="mt-1.5 text-xs text-foreground-600 md:text-sm">Locataires</p>
        </div>
        <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent-50 text-accent-700">
            <i className="ri-file-text-line text-lg" aria-hidden="true" />
          </span>
          <p className="mt-4 font-heading text-2xl font-semibold leading-none text-foreground-950">{contrats.length}</p>
          <p className="mt-1.5 text-xs text-foreground-600 md:text-sm">Contrats</p>
        </div>
        <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-background-100 text-foreground-600">
            <i className="ri-money-cny-circle-line text-lg" aria-hidden="true" />
          </span>
          <p className="mt-4 font-heading text-2xl font-semibold leading-none text-foreground-950">
            {formatFcfa(
              biens.reduce((total, bien) => total + bien.loyerMensuel, 0),
              { compact: true }
            )}
          </p>
          <p className="mt-1.5 text-xs text-foreground-600 md:text-sm">Loyers potentiels / mois</p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <section className="flex flex-col rounded-lg border border-background-200 bg-background-50 lg:col-span-2">
          <header className="flex items-center gap-2.5 border-b border-background-200 px-4 py-3.5 md:px-5">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-secondary-50 text-secondary-600">
              <i className="ri-information-line text-base" aria-hidden="true" />
            </span>
            <h3 className="font-heading text-sm font-semibold text-foreground-950 md:text-base">
              Informations de l'immeuble
            </h3>
          </header>
          <div className="p-4 md:p-5">
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {infos.map((info) => (
                <div key={info.label} className="flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-background-100 text-foreground-500">
                    <i className={`${info.icon} text-base`} aria-hidden="true" />
                  </span>
                  <div className="min-w-0">
                    <dt className="text-xs text-foreground-500">{info.label}</dt>
                    <dd className="truncate text-sm font-label font-medium text-foreground-950">{info.value}</dd>
                  </div>
                </div>
              ))}
            </dl>

            {proprietaire && (
              <div className="mt-5 border-t border-background-200 pt-4">
                <p className="text-xs font-label font-semibold uppercase tracking-wide text-foreground-400">
                  Propriétaire rattaché
                </p>
                <Link
                  to={`/agence/proprietaires/${proprietaire.id}`}
                  className="link-arrow mt-2 inline-flex items-center gap-2 rounded-md border border-foreground-200 bg-background-50 px-3.5 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:border-foreground-300 hover:bg-background-100 cursor-pointer"
                >
                  <i className="ri-user-star-line text-base" aria-hidden="true" />
                  {proprietaireNomComplet(proprietaire)}
                  <i className="ri-arrow-right-s-line text-base" aria-hidden="true" />
                </Link>
              </div>
            )}

            {immeuble.description && (
              <div className="mt-5 border-t border-background-200 pt-4">
                <p className="text-xs font-label font-semibold uppercase tracking-wide text-foreground-400">
                  Description
                </p>
                <p className="mt-2 text-sm leading-relaxed text-foreground-700">{immeuble.description}</p>
              </div>
            )}

            {immeuble.infosComplementaires && (
              <div className="mt-5 border-t border-background-200 pt-4">
                <p className="text-xs font-label font-semibold uppercase tracking-wide text-foreground-400">
                  Informations complémentaires
                </p>
                <p className="mt-2 text-sm leading-relaxed text-foreground-700">{immeuble.infosComplementaires}</p>
              </div>
            )}
          </div>
        </section>

        <section className="flex h-fit flex-col rounded-lg border border-background-200 bg-background-50">
          <header className="flex items-center gap-2.5 border-b border-background-200 px-4 py-3.5 md:px-5">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-accent-50 text-accent-700">
              <i className="ri-links-line text-base" aria-hidden="true" />
            </span>
            <h3 className="font-heading text-sm font-semibold text-foreground-950 md:text-base">Accès rapides</h3>
          </header>
          <div className="flex flex-col gap-1 p-2">
            {[
              { label: "Biens", to: `/agence/biens?immeuble=${immeuble.id}`, icon: "ri-home-4-line", hint: "Logements du bâtiment" },
              { label: "Locataires", to: `/agence/locataires?immeuble=${immeuble.id}`, icon: "ri-user-3-line", hint: "Fiches des occupants" },
              { label: "Contrats", to: `/agence/contrats?immeuble=${immeuble.id}`, icon: "ri-file-text-line", hint: "Baux et échéances" },
              { label: "Loyers", to: `/agence/loyers?immeuble=${immeuble.id}`, icon: "ri-wallet-3-line", hint: "Suivi par période" },
              { label: "Impayés", to: `/agence/impayes?immeuble=${immeuble.id}`, icon: "ri-alert-line", hint: "Loyers non réglés" },
              { label: "Dépenses", to: `/agence/depenses?immeuble=${immeuble.id}`, icon: "ri-money-cny-circle-line", hint: "Charges de l'immeuble" },
            ].map((module) => (
              <Link
                key={module.to}
                to={module.to}
                className="flex items-center gap-3 rounded-md px-3 py-2.5 transition-colors hover:bg-background-100 cursor-pointer"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-background-100 text-foreground-600">
                  <i className={`${module.icon} text-base`} aria-hidden="true" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-label font-medium text-foreground-950">
                    {module.label}
                  </span>
                  <span className="block truncate text-xs text-foreground-500">{module.hint}</span>
                </span>
                <i className="ri-arrow-right-s-line shrink-0 text-base text-foreground-400" aria-hidden="true" />
              </Link>
            ))}
          </div>
        </section>
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <section className="flex flex-col rounded-lg border border-background-200 bg-background-50">
          <header className="flex items-center justify-between gap-2.5 border-b border-background-200 px-4 py-3.5 md:px-5">
            <span className="flex items-center gap-2.5">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary-50 text-primary-600">
                <i className="ri-home-4-line text-base" aria-hidden="true" />
              </span>
              <h3 className="font-heading text-sm font-semibold text-foreground-950 md:text-base">Ses biens</h3>
            </span>
            <span className="rounded-full bg-background-100 px-2.5 py-1 text-xs font-label font-medium text-foreground-600">
              {biens.length}
            </span>
          </header>
          <div className="p-4 md:p-5">
            {biens.length === 0 ? (
              <div className="flex flex-col items-center justify-center rounded-md bg-background-100/70 px-6 py-8 text-center">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-100 text-primary-600">
                  <i className="ri-home-4-line text-xl" aria-hidden="true" />
                </span>
                <p className="mt-3 text-sm font-heading font-semibold text-foreground-950">Aucun bien rattaché</p>
                <p className="mt-1 max-w-xs text-xs text-foreground-600">
                  Ajoutez des appartements, studios ou boutiques à cet immeuble.
                </p>
                <Link
                  to={`/agence/biens?immeuble=${immeuble.id}&nouveau=1`}
                  className="mt-4 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-xs font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
                >
                  <i className="ri-add-line text-base" aria-hidden="true" />
                  Ajouter un bien
                </Link>
              </div>
            ) : (
              <ul className="flex flex-col divide-y divide-background-200">
                {biens.map((bien) => (
                  <li key={bien.id}>
                    <Link
                      to={`/agence/biens/${bien.id}`}
                      className="flex items-center justify-between gap-3 py-3 first:pt-0 last:pb-0 cursor-pointer"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm font-label font-medium text-foreground-950">
                          {bien.reference || AGENCE_BIEN_TYPE_LABELS[bien.type]}
                        </p>
                        <p className="mt-0.5 truncate text-xs text-foreground-500">
                          {AGENCE_BIEN_TYPE_LABELS[bien.type]} · {formatFcfa(bien.loyerMensuel)} / mois
                        </p>
                      </div>
                      <Badge tone={AGENCE_BIEN_STATUT_TONES[bien.statut]}>
                        {AGENCE_BIEN_STATUT_LABELS[bien.statut]}
                      </Badge>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        <section className="flex flex-col rounded-lg border border-background-200 bg-background-50">
          <header className="flex items-center justify-between gap-2.5 border-b border-background-200 px-4 py-3.5 md:px-5">
            <span className="flex items-center gap-2.5">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-accent-50 text-accent-700">
                <i className="ri-file-text-line text-base" aria-hidden="true" />
              </span>
              <h3 className="font-heading text-sm font-semibold text-foreground-950 md:text-base">Ses contrats</h3>
            </span>
            <span className="rounded-full bg-background-100 px-2.5 py-1 text-xs font-label font-medium text-foreground-600">
              {contrats.length}
            </span>
          </header>
          <div className="p-4 md:p-5">
            {contrats.length === 0 ? (
              <div className="flex flex-col items-center justify-center rounded-md bg-background-100/70 px-6 py-8 text-center">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-100 text-accent-700">
                  <i className="ri-file-text-line text-xl" aria-hidden="true" />
                </span>
                <p className="mt-3 text-sm font-heading font-semibold text-foreground-950">Aucun contrat</p>
                <p className="mt-1 max-w-xs text-xs text-foreground-600">
                  Les baux liés aux biens de cet immeuble apparaîtront ici.
                </p>
              </div>
            ) : (
              <ul className="flex flex-col divide-y divide-background-200">
                {contrats.map((contrat) => {
                  const bien = getBien(contrat.bienId);
                  const locataire = getLocataire(contrat.locataireId);
                  return (
                    <li key={contrat.id}>
                      <Link
                        to={`/agence/contrats/${contrat.id}`}
                        className="flex items-center justify-between gap-3 py-3 first:pt-0 last:pb-0 cursor-pointer"
                      >
                        <div className="min-w-0">
                          <p className="truncate text-sm font-label font-medium text-foreground-950">
                            {agenceContratReference(contrat.id)} · {bien?.reference || "Bien"}
                          </p>
                          <p className="mt-0.5 truncate text-xs text-foreground-500">
                            {locataire ? agenceLocataireNomComplet(locataire) : "Locataire non renseigné"} ·{" "}
                            {formatFcfa(contrat.loyerMensuel)} / mois
                          </p>
                        </div>
                        <Badge tone={AGENCE_CONTRAT_STATUT_TONES[contrat.statut]}>
                          {AGENCE_CONTRAT_STATUT_LABELS[contrat.statut]}
                        </Badge>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </section>
      </div>

      <ImmeubleForm open={editOpen} initial={immeuble} onClose={() => setEditOpen(false)} />

      <ConfirmDeleteModal
        open={toDelete}
        onClose={() => setToDelete(false)}
        onConfirm={() => {
          removeImmeuble(immeuble.id);
          navigate("/agence/immeubles");
        }}
        title="Supprimer l'immeuble"
        description={`Voulez-vous vraiment supprimer « ${immeuble.nom} » ? Cette action est définitive.`}
        blockedMessage={
          biensByImmeuble(immeuble.id).length > 0
            ? `Cet immeuble contient ${biensByImmeuble(immeuble.id).length} bien(s) rattaché(s). Supprimez ou déplacez d'abord ses biens.`
            : undefined
        }
      />
    </div>
  );
}